import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { sellerAPI, signedIn, signOut } from "../../services/portalApi";
import { cash, dateRange } from "../../services/api";
import { SignIn } from "../account/AccountPage";
import Footer from "../home/footer";
import PartnerBook from "./PartnerBook";
import PartnerBooking from "./PartnerBooking";
import "../booking/booking.css";
import "../account/account.css";
import "./partners.css";

const STATUS = {
  pending_payment: "Awaiting payment",
  confirmed: "Confirmed",
  requested: "Awaiting our quote",
  cancelled: "Cancelled",
  expired: "Hold expired",
};

/**
 * /partners — for people who sell Makua retreats for their clients.
 * Sign in with the emailed code (an account Makua's team set up), then:
 * your bookings, your link, what you've earned, and booking for a client.
 * Rooms are allocated, and medical screening handled, by Makua's team.
 */
export default function PartnersPage() {
  const [authed, setAuthed] = useState(signedIn());
  const [me, setMe] = useState(null);
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState("list");          // "list" | "new" | a booking ref
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setError(null);
    try {
      const [m, l] = await Promise.all([sellerAPI.me(), sellerAPI.list()]);
      setMe(m); setList(l);
    } catch (err) {
      if (err.status === 401) setAuthed(false);
      setError(err);
    }
  }, []);
  useEffect(() => { if (authed) load(); }, [authed, load]);

  const leave = () => { signOut(); setAuthed(false); setMe(null); setList(null); setError(null); };
  const go = (v) => { setView(v); setNotice(""); window.scrollTo({ top: 0 }); };

  let body;
  if (!authed) {
    body = (
      <SignIn onDone={() => setAuthed(true)} eyebrow="Partners" allowRegister={false}
        intro="Sign in with the email we set up for you. We'll send a code: there's no password." />
    );
  } else if (error && error.status === 403) {
    body = (
      <div className="mk-wrap">
        <p className="mk-eyebrow">Partners</p>
        <h1 className="mk-title">This area is for our partners.</h1>
        <p className="mk-muted">If you sell Makua retreats for your clients and need an account, write to us and we'll set one up.</p>
        <div className="mk-actions">
          <Link className="mk-btn mk-btn--primary" to="/contact">Contact us</Link>
          <button className="mk-btn mk-btn--ghost" onClick={leave}>Sign out</button>
        </div>
      </div>
    );
  } else if (error) {
    body = (
      <div className="mk-wrap">
        <p className="mk-error">{error.message}</p>
        <button className="mk-btn mk-btn--ghost" onClick={load}>Try again</button>
      </div>
    );
  } else if (!me || !list) {
    body = <div className="mk-wrap"><p className="mk-muted" style={{ minHeight: "60vh" }}>Loading…</p></div>;
  } else if (view === "new") {
    body = (
      <PartnerBook me={me} onCancel={() => go("list")}
        onBooked={(r) => { load(); setView(r.ref); setNotice(`Booked: ${r.ref}. We've emailed your client what's booked and how to pay.`); window.scrollTo({ top: 0 }); }} />
    );
  } else if (view !== "list") {
    body = <PartnerBooking refId={view} notice={notice} onBack={() => { go("list"); load(); }} />;
  } else {
    body = <Dashboard me={me.seller} list={list} onNew={() => go("new")} onOpen={(ref) => go(ref)} onLeave={leave} />;
  }

  return (
    <>
      <div className="mk-page grain-bg-light">{body}</div>
      <Footer />
    </>
  );
}

function Dashboard({ me, list, onNew, onOpen, onLeave }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try { navigator.clipboard.writeText(me.referralLink); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (_) { /* no clipboard */ }
  };
  const t = list.totals;
  const seesPayments = me.payment_visibility !== "none";
  return (
    <div className="mk-wrap mk-wrap--wide">
      <div className="pt-head">
        <div>
          <p className="mk-eyebrow">Partners</p>
          <h1 className="mk-title">{me.company || me.name}</h1>
          <p className="mk-muted mb-0">{me.company ? `${me.name} · ` : ""}{me.terms}</p>
        </div>
        <div className="pt-head-actions">
          <button className="mk-btn mk-btn--primary" onClick={onNew}>Book for a client</button>
          <button className="mk-btn mk-btn--ghost" onClick={onLeave}>Sign out</button>
        </div>
      </div>

      <dl className="pt-figures">
        <div><dt>Bookings</dt><dd>{t.live}</dd></div>
        <div><dt>Guests</dt><dd>{t.guests}</dd></div>
        <div><dt>Earned</dt><dd>{cash(t.earned)}</dd></div>
        <div><dt>Waiting on payment</dt><dd>{cash(t.pending)}</dd></div>
      </dl>

      <section className="pt-link" aria-labelledby="pt-link-title">
        <h2 id="pt-link-title" className="pt-h2">Your link</h2>
        <p className="mk-muted">Anyone who books within 30 days of arriving through it is credited to you.</p>
        <div className="pt-link-row">
          <code className="pt-code">{me.referralLink}</code>
          <button className="mk-btn mk-btn--ghost" onClick={copy}>{copied ? "Copied" : "Copy link"}</button>
        </div>
      </section>

      <section aria-labelledby="pt-list-title">
        <h2 id="pt-list-title" className="pt-h2">Your bookings</h2>
        {list.reservations.length === 0 ? (
          <p className="mk-muted">No bookings yet. Book for a client, or share your link.</p>
        ) : (
          <ul className="pt-list">
            {list.reservations.map((r) => (
              <li key={r.ref}>
                <button type="button" className="pt-row" onClick={() => onOpen(r.ref)}>
                  <span className="pt-row-main">
                    <span className="pt-when">{dateRange(r.arrive, r.depart)} · {r.ref}</span>
                    <span className="pt-name">{r.retreat}</span>
                    <span className="pt-meta">{r.client} · {r.guests} {r.guests === 1 ? "guest" : "guests"}{r.viaLink ? " · through your link" : ""}</span>
                  </span>
                  <span className="pt-row-side">
                    <span className={`pt-status is-${r.status}`}>{STATUS[r.status] || r.status}</span>
                    {seesPayments && r.payment && <span className="pt-meta">{r.payment}</span>}
                    <span className="pt-commission">{r.commissionState === "none" ? "No commission" : `${cash(r.commission)} ${r.commissionState}`}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
